import { LinkOutlined } from '@ant-design/icons';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

import { useTiptapEditor } from '../../use-tiptap-editor';
import { isMarkInSchema, sanitizeUrl } from '../../utils';

export interface UseLinkPopoverConfig {
  editor?: Editor | null;
  hideWhenUnavailable?: boolean;
  onSetLink?: () => void;
}

function canSetLink(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) {
    return false;
  }
  return editor.can().setMark('link');
}

function isLinkActive(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) {
    return false;
  }
  return editor.isActive('link');
}

function shouldShowLinkButton(props: { editor: Editor | null; hideWhenUnavailable: boolean }): boolean {
  const { editor, hideWhenUnavailable } = props;

  const linkInSchema = isMarkInSchema('link', editor);

  if (!linkInSchema || !editor) {
    return false;
  }

  if (hideWhenUnavailable && !editor.isActive('code')) {
    return canSetLink(editor);
  }

  return true;
}

function useLinkState(props: { editor: Editor | null; hideWhenUnavailable: boolean }) {
  const { editor, hideWhenUnavailable = false } = props;

  const canSet = canSetLink(editor);
  const isActive = isLinkActive(editor);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const handleSelectionUpdate = () => {
      setIsVisible(
        shouldShowLinkButton({
          editor,
          hideWhenUnavailable,
        }),
      );
    };

    handleSelectionUpdate();

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, hideWhenUnavailable]);

  return {
    isVisible,
    canSet,
    isActive,
  };
}

function useLinkHandler(props: Omit<UseLinkPopoverConfig, 'hideWhenUnavailable'>) {
  const { editor, onSetLink } = props;
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!editor) {
      return;
    }

    // Get URL immediately on mount
    const { href } = editor.getAttributes('link');

    if (isLinkActive(editor) && url === null) {
      setUrl(href || '');
    }
  }, [editor, url]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const updateLinkState = () => {
      const { href } = editor.getAttributes('link');
      setUrl(href || '');
    };

    editor.on('selectionUpdate', updateLinkState);
    return () => {
      editor.off('selectionUpdate', updateLinkState);
    };
  }, [editor]);

  const setLink = useCallback(() => {
    if (!url || !editor) {
      return;
    }

    const { selection } = editor.state;
    const isEmpty = selection.empty;

    let chain = editor.chain().focus();

    chain = chain.extendMarkRange('link').setLink({ href: url });

    if (isEmpty) {
      chain = chain.insertContent({ type: 'text', text: url });
    }

    chain.run();

    setUrl(null);

    onSetLink?.();
  }, [editor, onSetLink, url]);

  const removeLink = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.chain().focus().extendMarkRange('link').unsetLink().setMeta('preventAutolink', true).run();
    setUrl('');
  }, [editor]);

  const openLink = useCallback(
    (target = '_blank', features = 'noopener,noreferrer') => {
      if (!url) {
        return;
      }

      const safeUrl = sanitizeUrl(url, window.location.href);
      if (safeUrl !== '#') {
        window.open(safeUrl, target, features);
      }
    },
    [url],
  );

  return {
    url: url || '',
    setUrl,
    setLink,
    removeLink,
    openLink,
  };
}

export function useLinkPopover(props?: UseLinkPopoverConfig) {
  const { editor: providedEditor, hideWhenUnavailable = false, onSetLink } = props || {};
  const { editor } = useTiptapEditor(providedEditor);
  const { isVisible, canSet, isActive } = useLinkState({
    editor,
    hideWhenUnavailable,
  });
  const linkHandler = useLinkHandler({
    editor,
    onSetLink,
  });

  return {
    isVisible,
    canSet,
    isActive,
    label: 'Link',
    Icon: LinkOutlined,
    ...linkHandler,
  };
}
